import { APIProvider } from '@vis.gl/react-google-maps';
import { Loader2 } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

import { Card, CardContent } from '@/components/atoms/Card/Card';
import { useAddressGeocoding } from './hooks/useAddressGeocoding';
import { useStreetView } from './hooks/useStreetView';
import type { NormalizedAddress } from './utils';
import { extractPrimaryAddress } from './utils';

const StreetViewPanorama: React.FC<{
  position: google.maps.LatLngLiteral;
  heading: number;
}> = ({ position, heading }) => {
  const panoramaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!panoramaRef.current) return;

    const panorama = new google.maps.StreetViewPanorama(panoramaRef.current, {
      position,
      enableCloseButton: false,
      addressControl: true,
      fullscreenControl: true,
      panControl: true,
      zoomControl: true,
      pov: {
        heading,
        pitch: 10, // Slightly higher pitch to better see building fronts
      },
      zoom: 1.2, // Slightly closer zoom for better building detail
      scrollwheel: false,
      motionTracking: false, // Disable motion tracking for more stable view
      motionTrackingControl: false,
    });

    return () => {
      panorama.setVisible(false);
    };
  }, [position, heading]);

  return <div ref={panoramaRef} style={{ width: '100%', height: '100%' }} />;
};

type GoogleMapsViewProps = {
  address?: string | null;
  countryCode?: string;
  googleMapsApiKey: string;
  onAddressEnriched?: (address: NormalizedAddress) => void;
  className?: string;
};

const MapContent: React.FC<{
  address?: string | null;
  countryCode?: string;
  onAddressEnriched?: (address: NormalizedAddress) => void;
}> = ({ address, countryCode, onAddressEnriched }) => {
  const {
    position,
    isLoading: isGeocoding,
    shouldShow,
  } = useAddressGeocoding(address, countryCode, onAddressEnriched);
  const { panoramaPosition, status: streetViewStatus, heading } = useStreetView(position);

  if (!address || !shouldShow) {
    return null;
  }

  if (isGeocoding || !position) {
    return (
      <div className="flex h-full items-center justify-center rounded-md bg-gray-100">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Geocoding address...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {streetViewStatus === 'LOADING' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Loading Street View...</p>
          </div>
        </div>
      )}

      {streetViewStatus === 'OK' && panoramaPosition && (
        <StreetViewPanorama position={panoramaPosition} heading={heading} />
      )}

      {streetViewStatus === 'NOT_AVAILABLE' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
          <div className="rounded bg-white/90 p-4 text-center shadow">
            <p className="text-sm text-gray-700 mb-1">
              Street View is not available at this location.
            </p>
            <p className="text-xs text-gray-500">Address: {extractPrimaryAddress(address || '')}</p>
          </div>
        </div>
      )}

      {streetViewStatus === 'ERROR' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-20">
          <div className="rounded bg-red-50 p-4 text-center shadow">
            <p className="text-sm text-red-700">Error loading street view</p>
          </div>
        </div>
      )}
    </div>
  );
};

export const GoogleMapsView: React.FC<GoogleMapsViewProps> = ({
  address,
  countryCode,
  googleMapsApiKey,
  onAddressEnriched,
  className = '',
}) => {
  if (!googleMapsApiKey) {
    return (
      <Card className={`flex size-full items-center justify-center ${className}`}>
        <CardContent className="p-6 text-center text-red-600">
          <p>Google Maps API key is required to display address information.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div
      className={`${className} rounded-3xl overflow-hidden`}
      style={{ height: '300px', width: '100%' }}
    >
      <APIProvider apiKey={googleMapsApiKey} libraries={['geocoding', 'streetView', 'places']}>
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          <MapContent
            address={address}
            countryCode={countryCode}
            onAddressEnriched={onAddressEnriched}
          />
        </div>
      </APIProvider>
    </div>
  );
};
